/**
 * HotspotManager: spherical trigger hotspots with in-world markers + click ray.
 *
 * Each frame, checks the player's distance to every hotspot and emits
 * HOTSPOT_ENTER / HOTSPOT_EXIT via the EventBus. `onClick` ray-tests the
 * camera forward against hotspot spheres and emits HOTSPOT_CLICK for the
 * nearest hit.
 */

import { Entity, Quat, StandardMaterial, Vec3 } from 'playcanvas';
import type { Application } from 'playcanvas';
import { eventBus } from '../events/EventBus';
import type { HotspotConfig } from '../config/manifest';
import type { CameraState } from '../player/types';
import { worldIds } from '../render/LayerManager';

const fwd = new Vec3();
const toCenter = new Vec3();
const quat = new Quat();

export class HotspotManager {
    private _app: Application;
    private _hotspots: HotspotConfig[];
    private _inside = new Set<string>();
    private _markers: Entity[] = [];

    constructor(app: Application, hotspots: HotspotConfig[]) {
        this._app = app;
        this._hotspots = hotspots;
        this._buildMarkers();
    }

    private _buildMarkers() {
        for (const h of this._hotspots) this._buildMarker(h);
    }

    private _buildMarker(h: HotspotConfig) {
        const mat = new StandardMaterial();
        mat.emissive.set(h.color?.[0] ?? 1, h.color?.[1] ?? 1, h.color?.[2] ?? 1);
        mat.emissiveIntensity = 1.5;
        mat.diffuse.set(0, 0, 0);
        mat.useLighting = false;
        mat.depthTest = true;
        mat.update();
        const entity = new Entity(`hotspot-${h.id}`);
        entity.addComponent('render', { type: 'sphere' });
        entity.render!.material = mat;
        entity.render!.layers = worldIds();
        entity.setLocalScale(0.24, 0.24, 0.24);
        entity.setPosition(h.position[0], h.position[1], h.position[2]);
        this._app.root.addChild(entity);
        this._markers.push(entity);
    }

    /** Add a hotspot at runtime (used by the trigger-point editor). */
    addHotspot(h: HotspotConfig) {
        this._hotspots.push(h);
        this._buildMarker(h);
    }

    /** Per-frame proximity check. `playerPosition` is world space. */
    update(playerPosition: Vec3) {
        for (const h of this._hotspots) {
            toCenter.set(h.position[0] - playerPosition.x,
                h.position[1] - playerPosition.y,
                h.position[2] - playerPosition.z);
            const dist = toCenter.length();
            const inside = dist <= h.radius;
            const wasInside = this._inside.has(h.id);
            if (inside && !wasInside) {
                this._inside.add(h.id);
                eventBus.emit({ type: 'HOTSPOT_ENTER', id: h.id, name: h.name });
            } else if (!inside && wasInside) {
                this._inside.delete(h.id);
                eventBus.emit({ type: 'HOTSPOT_EXIT', id: h.id, name: h.name });
            }
        }
    }

    /**
     * Click handler: ray-test camera forward against hotspot spheres; if no
     * precise hit, fall back to the nearest hotspot the player is standing
     * inside. Emits HOTSPOT_CLICK for the UI popup. Returns the id or null.
     */
    onClick(state: CameraState, maxDist = 30): string | null {
        quat.setFromEulerAngles(state.angles);
        quat.transformVector(Vec3.FORWARD, fwd);
        return this.onClickRay(state.position.x, state.position.y, state.position.z, fwd.x, fwd.y, fwd.z, maxDist);
    }

    /**
     * Ray-based click handler: ray-test an arbitrary world-space ray against
     * hotspot spheres; if no precise hit, fall back to the nearest hotspot
     * the player is standing inside. Used for mouse-cursor-driven clicks.
     */
    onClickRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist = 30): string | null {
        let bestId: string | null = null;
        let bestT = Infinity;

        for (const h of this._hotspots) {
            toCenter.set(h.position[0] - ox, h.position[1] - oy, h.position[2] - oz);
            const t = dx * toCenter.x + dy * toCenter.y + dz * toCenter.z;
            if (t < 0 || t > maxDist) continue;
            const closestX = ox + dx * t;
            const closestY = oy + dy * t;
            const closestZ = oz + dz * t;
            const rx = closestX - h.position[0];
            const ry = closestY - h.position[1];
            const rz = closestZ - h.position[2];
            const distSq = rx * rx + ry * ry + rz * rz;
            if (distSq <= h.radius * h.radius && t < bestT) {
                bestT = t;
                bestId = h.id;
            }
        }

        if (bestId) {
            eventBus.emit({ type: 'HOTSPOT_CLICK', id: bestId });
        }
        return bestId;
    }

    /** Hide/show all markers (e.g. toggle). */
    setMarkersVisible(visible: boolean) {
        for (const m of this._markers) m.enabled = visible;
    }

    /** Remove all marker entities (on scene unload). */
    destroy() {
        for (const m of this._markers) {
            this._app.root.removeChild(m);
            m.destroy();
        }
        this._markers.length = 0;
        this._inside.clear();
    }
}
